// SendWarnings.tsx - a send's server warnings, one row each, styled by severity:
// the send dialog's list, and the assistant's flow card.
import React from "react";
import { Info, ShieldAlert, TriangleAlert, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DraftWarning, WarningSeverity } from "../types";

const SEVERITY_DISPLAY: Record<WarningSeverity, { icon: LucideIcon; className: string }> = {
  info: { icon: Info, className: "border-border bg-muted/40 text-foreground" },
  warning: {
    icon: TriangleAlert,
    className:
      "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-50",
  },
  compliance: {
    icon: ShieldAlert,
    className: "border-destructive/50 bg-destructive/10 text-destructive",
  },
};

export interface SendWarningsProps {
  warnings: DraftWarning[];
  /** The rows' text size: the dialog's `text-sm`, or a card's smaller one. */
  size?: "sm" | "xs";
}

/** Nothing without warnings. */
const SendWarnings: React.FC<SendWarningsProps> = ({ warnings, size = "sm" }) =>
  warnings.length > 0 ? (
    <ul className="space-y-2" aria-label="Warnings">
      {warnings.map((warning, index) => {
        const { icon: Icon, className: tone } =
          SEVERITY_DISPLAY[warning.severity] ?? SEVERITY_DISPLAY.warning;
        return (
          <li
            key={`${warning.code}-${index}`}
            data-severity={warning.severity}
            className={cn(
              "flex gap-2 rounded-md border px-3 py-2",
              size === "sm" ? "text-sm" : "text-xs",
              tone
            )}
          >
            <Icon
              className={cn("shrink-0", size === "sm" ? "mt-0.5 h-4 w-4" : "h-3.5 w-3.5")}
              aria-hidden
            />
            <span>{warning.message}</span>
          </li>
        );
      })}
    </ul>
  ) : null;

export default SendWarnings;
